import { makeAutoObservable, runInAction } from 'mobx';

import { client } from 'contexts/ApolloClient/apolloClient';
import { standardError } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { FETCH_CREW_MEMBER_ACTION_LOG } from 'pages/CrewMemberDetails/gql/getCrewMemberActionLog';

import { WA_LOG_TYPES } from './const';
import { typesGrouped } from './groupedFilterTypes';

/**
 * @typedef {Object} LogEntry
 * @property {string} id
 * @property {string} type
 * @property {string} createdAt
 * @property {string} createdBy
 * @property {{ __typename: string } & Record<string, any>} parameters
 *
 * @typedef {Object} FilterType
 * @property {string} name
 * @property {string[]} value
 *
 * @typedef {Object} DateRangeFilter
 * @property {string} name
 * @property {Object} [value]
 */

class CrewMemberActionLogStore {
	logs = [];
	crewMemberId = '';
	currentPage = 0;
	currentSize = 10;
	filterType = typesGrouped.all;
	filterDaterange = { name: 'Past Month' };

	totalPages = null;
	totalCount = null;
	size = null;

	isLoading = false;

	constructor() {
		makeAutoObservable(this);
	}

	initialize = (userId) => {
		this.crewMemberId = userId;
		this.fetchLog();
	};

	fetchLog = async () => {
		this.isLoading = true;
		try {
			const variables = {
				filter: {
					crewMemberIds: [this.crewMemberId],
					actionLogType: this.filterType.value,
				},
				pagination: {
					page: this.currentPage,
					size: this.currentSize,
				},
			};
			if (this.filterDaterange !== null || this.filterDaterange?.name !== 'Past Month') {
				variables.filter = { ...variables.filter, ...this.filterDaterange.value };
			}

			const response = await client.query({
				query: FETCH_CREW_MEMBER_ACTION_LOG,
				variables,
				fetchPolicy: 'no-cache',
			});

			runInAction(() => {
				this.logs = CrewMemberActionLogStore.groupLogs(
					response.data.searchActionLogsByCrewMemberIds.results
				);
				this.totalCount = response.data.searchActionLogsByCrewMemberIds.page.totalCount;
				this.totalPages = response.data.searchActionLogsByCrewMemberIds.page.totalPages;
				this.size = response.data.searchActionLogsByCrewMemberIds.page.size;
				this.isLoading = false;
			});
		} catch (e) {
			notificationStore.setNotification(standardError);
		}
	};

	get isEmptyLog() {
		return this.totalCount === 0 && this.totalCount !== null;
	}

	static WA_TYPE_VALUES = new Set(Object.values(WA_LOG_TYPES));

	static groupLogs(results) {
		const grouped = [];
		const waGroups = new Map();

		for (const entry of results) {
			if (!CrewMemberActionLogStore.WA_TYPE_VALUES.has(entry.type)) {
				grouped.push(entry);
				continue;
			}
			const key = `${entry.type}|${entry.createdBy}`;
			if (!waGroups.has(key)) {
				const group = { ...entry, parameters: [entry.parameters] };
				waGroups.set(key, group);
				grouped.push(group);
			} else {
				waGroups.get(key).parameters.push(entry.parameters);
			}
		}

		return grouped;
	}

	setCurrentPage = (page) => {
		this.currentPage = page;
		this.fetchLog();
	};

	setSelectedTypes = (type) => {
		this.filterType = type;
		this.fetchLog();
	};

	setSelectedDaterange = (daterange) => {
		this.filterDaterange = daterange;
		this.fetchLog();
	};
}

export const crewMemberActionLogStore = new CrewMemberActionLogStore();
